import type { AbsenceCode, Cell } from '@tabel/shared/schemas/cells';
import { PenLine, RefreshCw } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import type { CellBase } from '../lib/api';
import { ApiError } from '../lib/errors';
import { type HoursFormat, hoursText, ruDate, shownHours, wholeHours } from '../lib/format';
import { type ToastAction, useToast } from './ErrorToast';
import { UnsavedMark, useUnsavedChanges } from './UnsavedGuard';
import { Button, Callout, inputCls, textareaCls } from './ui';

/*
 * Правка часов в карточке дня (FR-005; 001 FR-020, FR-026; contracts/ui-panel.md, «Подвал»): часы от 0 до 24 в формате
 * часов правил объекта, комментарий обязателен (до 500 знаков). Машинные часы остаются видны рядом с правкой.
 * Ввод — несохранённое (`UnsavedGuard`): закрытие и переход спрашивают «Сохранить правку?». В запросе — `base`:
 * состояние дня, с которым открыта форма. На `409 STALE_STATE` — «День изменился» и новое состояние рядом с формой;
 * при любой ошибке сохранения ввод остаётся, тост — с «Повторить».
 * Состояние формы держит `useCellEdit`, поля — `CellEditForm`, кнопки подвала панели — `CellEditFooter`.
 */

/** Подсказка под полем часов (правка часов и ответ на вопрос часами). */
export function hoursHint(f: HoursFormat | null): string {
  if (f?.kind === 'hmm') return 'От 0:00 до 24:00, например 8:30. Минуты — кратно 6.';
  if (wholeHours(f)) return 'Целые, от 0 до 24.';
  return 'От 0 до 24, с шагом 0,1. Например, 8,5.';
}

/** Разбор ввода: «8», «8,5», «8.5», «8:30». Ошибка — что сделать и пример. */
export function parseHours(text: string, f: HoursFormat | null): { hours: number } | { error: string } {
  const s = text.trim().replace(/\s+/g, '');
  if (!s) return { error: 'Введите часы, например 8' };
  let h: number;
  const hm = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (hm) {
    const min = Number(hm[2]);
    if (min >= 60) return { error: 'Минуты — от 00 до 59, например 8:30' };
    h = Number(hm[1]) + min / 60;
  } else if (/^\d{1,2}(?:[.,]\d+)?$/.test(s)) {
    h = Number(s.replace(',', '.'));
  } else {
    return { error: `Не понятно число. ${hoursHint(f)}` };
  }
  if (h < 0 || h > 24) return { error: 'Часы — от 0 до 24' };
  const units = wholeHours(f) ? h : h * 10;
  if (Math.abs(units - Math.round(units)) > 1e-9) return { error: hoursHint(f) };
  return { hours: wholeHours(f) ? Math.round(h) : Math.round(h * 10) / 10 };
}

/**
 * Значение в поле при открытии: правка, иначе машинные часы с шагом ввода (0,1 ч или целые: правка вводится с этим
 * шагом), иначе пусто.
 */
function initialText(cell: Cell, f: HoursFormat | null): string {
  if (cell.manualHours != null) return hoursText(cell.manualHours, f);
  if (cell.machineHours == null) return '';
  const h = wholeHours(f) ? Math.round(cell.machineHours) : Math.round(cell.machineHours * 10) / 10;
  return hoursText(h, f);
}

/** Состояние дня из `details.current` ответа `STALE_STATE` (там текущая ячейка, T025); другой формы — `null`. */
function baseFrom(details: unknown): CellBase | null {
  const cur = (details as { current?: unknown } | null | undefined)?.current;
  if (typeof cur !== 'object' || cur === null) return null;
  const c = cur as Record<string, unknown>;
  if (!('manualHours' in c) && !('absenceCode' in c)) return null;
  const manualHours = typeof c.manualHours === 'number' ? c.manualHours : null;
  const absenceCode = typeof c.absenceCode === 'string' ? (c.absenceCode as AbsenceCode) : null;
  return { manualHours, absenceCode };
}

export const baseOf = (c: Cell): CellBase => ({ manualHours: c.manualHours, absenceCode: c.absenceCode });

/** Состояние дня словами: «правка 9 ч», «код отсутствия О», «правки нет». */
function describeBase(b: CellBase, f: HoursFormat | null): string {
  const parts: string[] = [];
  if (b.absenceCode) parts.push(`код отсутствия ${b.absenceCode}`);
  if (b.manualHours != null) parts.push(`правка ${hoursText(b.manualHours, f)} ч`);
  return parts.length > 0 ? parts.join(', ') : 'правки и кода отсутствия нет';
}

interface Draft {
  /** День формы: `personId|date`. Другая ячейка — формы нет. */
  key: string;
  text: string;
  comment: string;
  /** Начальные значения: несохранённое — только если ввод от них отличается. */
  initText: string;
  initComment: string;
  /** Состояние дня при открытии формы — в запрос (`base`). */
  base: CellBase;
  /** Сервер ответил `STALE_STATE`: новое состояние дня (`null` — взять из ячейки после обновления). */
  stale: { current: CellBase | null } | null;
}

export interface CellEditApi {
  open: boolean;
  /** Открыть форму для текущей ячейки. */
  start: () => void;
  /** «Отмена»: закрыть без записи. */
  cancel: () => void;
  text: string;
  comment: string;
  setText: (v: string) => void;
  setComment: (v: string) => void;
  /** Ошибка ввода часов: показывается, когда поле изменили. */
  hoursError: string | null;
  dirty: boolean;
  canSave: boolean;
  busy: boolean;
  /** Сохранить. Ошибка — исключение: форма открыта, ввод остался. */
  submit: () => Promise<void>;
  /** Новое состояние дня после `STALE_STATE` словами; иначе `null`. */
  staleText: string | null;
  format: HoursFormat | null;
  machineText: string;
  displayName: string;
  date: string;
}

/**
 * Состояние формы правки одной ячейки карточки дня. `key` — день (`personId|date`): форма другой ячейки закрыта.
 * `save` — запрос правки; бросает ошибку — форма остаётся открытой.
 */
export function useCellEdit({
  key,
  cell,
  displayName,
  format,
  save,
  screen,
  undo,
}: {
  key: string;
  cell: Cell | null;
  displayName: string;
  format: HoursFormat | null;
  save: ((hours: number, comment: string, base: CellBase) => Promise<unknown>) | undefined;
  /** Экран для журнала ошибок интерфейса. */
  screen: string;
  /** «Отменить» в тосте «Правка сохранена» по ответу сервера (003, FR-038); нет `undoActionId` — без кнопки. */
  undo?: (res: unknown) => ToastAction | undefined;
}): CellEditApi {
  const { showError, showInfo } = useToast();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const d = draft && draft.key === key && cell ? draft : null;
  const parsed = d ? parseHours(d.text, format) : null;
  const comment = d?.comment.trim() ?? '';
  const canSave = Boolean(parsed && 'hours' in parsed && comment.length > 0 && save);
  const dirty = Boolean(d && (d.text.trim() !== d.initText.trim() || comment !== d.initComment.trim()));
  const cellRef = useRef(cell);
  cellRef.current = cell;

  const close = () => {
    setDraft(null);
    setTouched(false);
  };

  const submit = async () => {
    const cur = d;
    const p = cur ? parseHours(cur.text, format) : null;
    const text = cur?.comment.trim() ?? '';
    if (!cur || !save || !p || !('hours' in p) || !text) throw new Error('Правка не дописана');
    // После «День изменился» человек видел новое состояние: следующая попытка сверяет уже с ним.
    const latest = cellRef.current;
    const base = cur.stale ? (cur.stale.current ?? (latest ? baseOf(latest) : cur.base)) : cur.base;
    setBusy(true);
    let res: unknown;
    try {
      res = await save(p.hours, text, base);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'STALE_STATE') {
        const current = baseFrom(e.details);
        setDraft((x) => (x && x.key === cur.key ? { ...x, stale: { current } } : x));
      } else {
        showError(e, {
          screen,
          action: 'edit',
          lead: 'Не сохранено',
          note: 'Введённое осталось в панели.',
          retry: () => void latestSubmit.current().catch(() => {}),
        });
      }
      throw e;
    } finally {
      setBusy(false);
    }
    close();
    showInfo('Правка сохранена', { action: undo?.(res) });
  };
  const latestSubmit = useRef(submit);
  latestSubmit.current = submit;

  const hoursValue = parsed && 'hours' in parsed ? hoursText(parsed.hours, format) : (d?.text.trim() ?? '');
  useUnsavedChanges(
    d && dirty
      ? {
          kind: 'edit',
          summary: `${displayName}, ${ruDate(cell?.date ?? '').slice(0, 5)}: ${hoursValue || '—'} ч`,
          canSave,
          save: () => latestSubmit.current(),
          discard: close,
        }
      : null,
  );

  const staleBase = d?.stale ? (d.stale.current ?? (cell ? baseOf(cell) : null)) : null;
  return {
    open: d !== null,
    start: () => {
      if (!cell) return;
      const initText = initialText(cell, format);
      const initComment = cell.manualComment ?? '';
      setTouched(false);
      setDraft({
        key,
        text: initText,
        comment: initComment,
        initText,
        initComment,
        base: baseOf(cell),
        stale: null,
      });
    },
    cancel: close,
    text: d?.text ?? '',
    comment: d?.comment ?? '',
    setText: (v) => {
      setTouched(true);
      setDraft((x) => (x ? { ...x, text: v } : x));
    },
    setComment: (v) => setDraft((x) => (x ? { ...x, comment: v } : x)),
    hoursError: touched && parsed && 'error' in parsed ? parsed.error : null,
    dirty,
    canSave,
    busy,
    submit,
    staleText: staleBase ? describeBase(staleBase, format) : null,
    format,
    machineText: cell?.machineHours != null ? `${shownHours(cell.machineValue, cell.machineHours)} ч` : 'нет',
    displayName,
    date: cell?.date ?? '',
  };
}

/**
 * Поля правки часов в карточке дня: часы, комментарий, «Машинные часы останутся видны». После `STALE_STATE` —
 * «День изменился» и новое состояние дня над полями; введённое остаётся.
 */
export function CellEditForm({ edit }: { edit: CellEditApi }) {
  const hoursId = useId();
  const commentId = useId();
  const hintId = useId();
  const hoursRef = useRef<HTMLInputElement>(null);
  // Открылась форма — фокус в поле часов, текст выделен: обычно число вводят заново.
  useEffect(() => {
    if (!edit.open) return;
    const el = hoursRef.current;
    el?.focus();
    el?.select();
  }, [edit.open]);
  if (!edit.open) return null;
  return (
    <section
      aria-label="Правка часов"
      className="rounded-card border border-accent/25 bg-accent-soft/40 px-4 py-3.5 max-sm:px-3"
    >
      <h3 className="mb-3 flex items-center gap-1.5 text-[14px] font-semibold text-ink">
        <PenLine size={16} strokeWidth={1.75} className="text-ink-3" aria-hidden />
        Правка часов
      </h3>
      {edit.staleText && (
        <div className="mb-3">
          <Callout tone="warn" icon={RefreshCw}>
            <p className="font-semibold">День изменился, пока была открыта форма</p>
            <p className="mt-0.5">
              Сейчас: {edit.staleText}. Введённое осталось. Проверьте и сохраните ещё раз.
            </p>
          </Callout>
        </div>
      )}
      <div className="grid grid-cols-[minmax(0,140px)_minmax(0,1fr)] gap-x-3 max-sm:grid-cols-1">
        <div className="mb-3">
          <label htmlFor={hoursId} className="mb-1.5 block text-[13px] font-medium text-ink">
            Часы
          </label>
          <input
            ref={hoursRef}
            id={hoursId}
            // В панели до 900 px поле не ниже 40 px (T103).
            className={`${inputCls} tabular-nums max-tablet:min-h-10`}
            inputMode={edit.format?.kind === 'hmm' ? 'text' : 'decimal'}
            autoComplete="off"
            value={edit.text}
            aria-invalid={edit.hoursError ? true : undefined}
            aria-describedby={`${hoursId}-hint`}
            onChange={(e) => edit.setText(e.target.value)}
          />
          <span
            id={`${hoursId}-hint`}
            className={`mt-1 block text-[12px] ${edit.hoursError ? 'text-bad' : 'text-ink-3'}`}
          >
            {edit.hoursError ?? `${hoursHint(edit.format)} Машинные: ${edit.machineText}.`}
          </span>
        </div>
        <div className="mb-3">
          <label htmlFor={commentId} className="mb-1.5 block text-[13px] font-medium text-ink">
            Комментарий (обязательно)
          </label>
          <textarea
            id={commentId}
            className={`${textareaCls} h-20 resize-y`}
            value={edit.comment}
            maxLength={500}
            aria-describedby={hintId}
            placeholder="Например: нет прохода на выходе, выход в 18:00"
            onChange={(e) => edit.setComment(e.target.value)}
          />
          <span id={hintId} className="mt-1 block text-[12px] text-ink-3">
            Без комментария сохранить нельзя: почему часы отличаются от машинных.
          </span>
        </div>
      </div>
      <p className="text-[12px] text-ink-2">
        Машинные часы останутся видны рядом с правкой. Правка пишется в журнал действий.
      </p>
    </section>
  );
}

/** Подвал панели, пока открыта правка: «Не сохранено» · «Отмена» · «Сохранить правку». */
export function CellEditFooter({ edit }: { edit: CellEditApi }) {
  return (
    <>
      {edit.dirty && <UnsavedMark />}
      <span className="flex-1" aria-hidden />
      <Button onClick={edit.cancel} disabled={edit.busy}>
        Отмена
      </Button>
      <Button
        variant="primary"
        disabled={!edit.canSave || edit.busy}
        onClick={() => void edit.submit().catch(() => {})}
      >
        Сохранить правку
      </Button>
    </>
  );
}
